import withRouter from "Components/Common/withRouter"
import ContinuingEducationForm from "Components/organisms/forms/continuing-education-form"
import ContinuingEducationFields from "Components/organisms/forms/continuing-education-form/types"
import FormPageTemplate from "Components/templates/form-page-template"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import { ContinuingEducationValidation } from "data/continuing-education"
import { FormikHelpers, useFormik } from "formik"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ProfileSliceState } from "slices/auth/profile/reducer"
import { addContinuingEducationRequest, ContinuingEducationSliceState, setContinuingEducationFormFields } from "slices/forms/continuing-education/reducer"
import { getContinuingEducationFormFields, getContinuingEducationRemainingBudget, submitCreateContinuingEducationForm } from "slices/forms/continuing-education/thunk"
import ContinuingEducationEnvironment from "models/continuing-education-environment"
import ContinuingEducationType from "models/continuing-education-type"
import ContinuingEducationTopic from "models/continuing-education-topic"
import Modal from "Components/molecule/modal";
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import { navigateBackOr, navigateToContinuingEducationList } from "utils/navigator"

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({
        employee
    })
)

const selectFormFields = createSelector(
    (state: any) => state.ContinuingEducation,
    ({ environments, types, topics }: ContinuingEducationSliceState) => ({
        environments,
        types,
        topics
    })
)

const CreateContinuingEducationPage = (props: any) => {
    document.title = "Create Continuing Education Request | IbexIQ";
    const { handleOpenManual } = useManual("forms/continuing-education");
    const dispatch: any = useDispatch();
    const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
    const { employee } = useSelector(selectProfile)
    const { environments, types, topics } = useSelector(selectFormFields)

    const initializePage = async () => {
        if (environments.length === 0 || types.length === 0 || topics.length === 0) {
            const res = await getContinuingEducationFormFields()
            if (!res) return;
            dispatch(setContinuingEducationFormFields(res))
        }
    }

    useEffect(() => {
        initializePage()
    }, [])

    const environmentOptions = useMemo(() => environments.map((e: ContinuingEducationEnvironment) => ({
        value: e.id,
        label: e.description
    })), [environments])

    const typeOptions = useMemo(() => types.map((t: ContinuingEducationType) => ({
        value: t.id,
        label: t.description
    })), [types])

    const topicOptions = useMemo(() => topics.map((t: ContinuingEducationTopic) => ({
        value: t.id,
        label: t.description
    })), [topics])

    const employeeName = employee
        ? (employee.commonName ?? employee.firstName) + " " + employee.lastName
        : ""

    const handleSubmitContinuingEducationForm = (_vals: ContinuingEducationFields, helpers: FormikHelpers<ContinuingEducationFields>) => {
        helpers.setSubmitting(false)
        setShowAreYouSureModal(true)
    }

    const initialValues: ContinuingEducationFields = useMemo(() => ({
        courseName: '',
        courseLink: '',
        courseHours: '',
        courseStartDate: '',
        courseEndDate: '',
        courseDescription: '',
        cost: '',
        environmentId: '',
        typeId: '',
        topicId: ''
    }), [])

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        isValid,
        setSubmitting,
        isSubmitting,
        handleReset
    } = useFormik({
        onSubmit: handleSubmitContinuingEducationForm,
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: ContinuingEducationValidation
    })

    const [remainingBudget, setRemainingBudget] = useState<number | null>(null)

    useEffect(() => {
        if (!values.courseEndDate) {
            setRemainingBudget(null)
            return;
        }

        getContinuingEducationRemainingBudget(values.courseEndDate).then(setRemainingBudget)
    }, [values.courseEndDate])

    const secondaryApprovalWarning = ((): string | null => {
        if (remainingBudget === null || values.cost === '') return null;
        if (Number(values.cost) <= remainingBudget) return null;

        return `This cost is over your remaining budget of $${remainingBudget.toLocaleString()} CAD — a Secondary Approver will be required.`;
    })()

    const handleBack = () => navigateBackOr(props.router.navigate, () => navigateToContinuingEducationList(props.router.navigate))

    const handleModalClose = () => setShowAreYouSureModal(false);

    const handleConfirmSubmitContinuingEducationForm = useCallback(async (values: ContinuingEducationFields, setSubmitting: (val: boolean) => any, reset: (e: any) => void) => {
        setSubmitting(true);
        const res = await submitCreateContinuingEducationForm(values)

        if (!res) {
            handleModalClose()
        } else {
            reset({})
            handleBack()
            dispatch(addContinuingEducationRequest(res))
        }
        setSubmitting(false)
    }, [handleBack, handleModalClose])

    return <FormPageTemplate
        title={"Forms"}
        breadcrumbItem={"Create Continuing Education Request"}
        actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
        form={<ContinuingEducationForm
            employeeName={employeeName}
            secondaryApprovalWarning={secondaryApprovalWarning}
            environments={environmentOptions}
            types={typeOptions}
            topics={topicOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
        />}
        isLoading={false}
        modal={<Modal
            isOpen={showAreYouSureModal}
            onClose={handleModalClose}
            title={"Confirm"}
            subtitle={"Are you sure you want to submit this Continuing Education request?"}
        >
            <AreYouSureButtons
                disable={isSubmitting}
                acceptButtonStyle={{ width: '150px' }}
                declineButtonStyle={{ width: '150px' }}
                handleAccept={() => handleConfirmSubmitContinuingEducationForm(values, setSubmitting, handleReset)}
                handleDecline={handleModalClose}
                acceptText={isSubmitting ? "Submitting..." : "Submit"}
                declineText={"Cancel"}
            />
        </Modal>}
    />
}

export default withRouter(CreateContinuingEducationPage);
